import type { Pinia } from 'pinia'
import { defineNuxtPlugin } from '#app'
import { createDate } from '~/composables/prebooking-date'
import { useAppStore } from '~/stores/appstore'
import { useDateTimeStore } from '~/stores/datetime'
import { useExtrasStore } from '~/stores/extras'
import { useLocationStore } from '~/stores/location'
import { useOfferStore } from '~/stores/offer'
import { usePaymentProfileStore } from '~/stores/payment-profile'
import { usePersonalStore } from '~/stores/personal'
import { useReferralStore } from '~/stores/referral'
import { useStepStore } from '~/stores/step'
import { useVehiclesStore } from '~/stores/vehicles'

const STORES_TO_PERSIST = [
  'date-time',
  'location',
  'vehicles',
  'referral',
  'extras',
  'offer',
  'payment-profile',
  'step',
  'app',
  'personal',
]

const STORAGE_KEY = 'pinia_state'

export default defineNuxtPlugin(({ $pinia }) => {
  // eslint-disable-next-line node/prefer-global/process
  if (process.server)
    return

  const pinia = $pinia as Pinia

  // Ensure all stores are instantiated before restoring (Pinia lazy-creates stores on first use)
  const dateTimeStore = useDateTimeStore()
  useLocationStore()
  useVehiclesStore()
  useReferralStore()
  useExtrasStore()
  useOfferStore()
  usePaymentProfileStore()
  useStepStore()
  useAppStore()
  usePersonalStore()

  // Restore state from sessionStorage on app initialization
  const savedState = sessionStorage.getItem(STORAGE_KEY)
  if (savedState) {
    try {
      const state = JSON.parse(savedState)
      Object.keys(state).forEach((storeName) => {
        if (pinia.state.value[storeName]) {
          if (storeName === 'date-time') {
            // PrebookingDate is a class — JSON round-trip strips the prototype (getters/setters).
            // Reconstruct instances explicitly using createDate(), same pattern as Referral.vue.
            // _date is the private backing field; must match the field name in prebooking-date.ts.
            dateTimeStore.$patch({
              startDate: createDate(state[storeName].startDate?._date),
              endDate: createDate(state[storeName].endDate?._date),
            })
          }
          else {
            // Restore state property-by-property to maintain ref reactivity
            const storeState = pinia.state.value[storeName]
            Object.keys(state[storeName]).forEach((key) => {
              storeState[key] = state[storeName][key]
            })
          }
        }
      })
    }
    catch (e) {
      console.error('[pinia-persist] Failed to restore state:', e)
    }
  }

  // Setup function to persist state before redirects
  const persistState = () => {
    try {
      const stateToPersist: Record<string, any> = {}
      STORES_TO_PERSIST.forEach((storeName) => {
        if (pinia.state.value[storeName]) {
          try {
            stateToPersist[storeName] = storeName === 'date-time'
              // date-time: the restore reads only these two, pinned to rental TZ
              ? { startDate: dateTimeStore.startDate.toPersisted(), endDate: dateTimeStore.endDate.toPersisted() }
              // JSON.parse(JSON.stringify()) serializes reactive objects properly
              : JSON.parse(JSON.stringify(pinia.state.value[storeName]))
          }
          catch (storeError) {
            console.warn(`[pinia-persist] Failed to serialize store "${storeName}":`, storeError)
            stateToPersist[storeName] = pinia.state.value[storeName]
          }
        }
      })
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(stateToPersist))
    }
    catch (e) {
      console.error('[pinia-persist] Failed to persist state:', e)
    }
  }

  // Drop the persisted snapshot entirely — used after a booking completes so a
  // stale pre-booking snapshot can't be rehydrated if the user navigates back
  // into the flow within the same tab (CCT-2060)
  const clearPersistedState = () => {
    try {
      sessionStorage.removeItem(STORAGE_KEY)
    }
    catch (e) {
      console.error('[pinia-persist] Failed to clear persisted state:', e)
    }
  }

  return {
    provide: {
      persistPiniaState: persistState,
      clearPersistedState,
    },
  }
})
